import UserConfirmDialog from "@/components/UserManagementComponents/Dialogs/userConfirmDialog.component";
import UserMergeDialog from "@/components/UserManagementComponents/Dialogs/userMergeDialog.component";
import UserRejectionDialog from "@/components/UserManagementComponents/Dialogs/userRejectionDialog.component";
import {
  ROUTE_MERGE_COMPANIES,
  ROUTE_PARAM_MERGE_COMPANIES_USER_ID_KEEP,
} from "@/constants/routes.constants";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import { RoleName } from "@/types";
import {
  CheckCheck,
  CircleMinus,
  Ellipsis,
  Merge,
  UserCog,
} from "lucide-react";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router";

type UserApprovalRejectionProps = {
  userFullName: string;
  userID: string;
  userRoleName: RoleName;
  updateRole: (
    userId: string,
    roleAction: "approve" | "block" | "admin"
  ) => void;
};

const UserApprovalRejection: React.FC<UserApprovalRejectionProps> = ({
  userFullName,
  userID,
  userRoleName,
  updateRole,
}) => {
  const { userRole } = useContext(AppContext);
  const nav = useNavigate();

  const [showRejectionDialog, setShowRejectionDialog] =
    useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<"approve" | "admin" | false>(
    false
  );
  const [showMergeDialog, setShowMergeDialog] = useState<boolean>(false);

  // Define available actions based on user role
  const getAvailableActions = (roleName: RoleName) => {
    if (!userRole && userRole !== "admin" && userRole !== "super_admin") {
      console.error(
        "User role is not defined or user does not have permission to perform actions."
      );
      return [];
    }

    switch (roleName) {
      case "admin":
        return [{ label: "Block", value: "block" }];
      case "super_admin":
        return []; // No actions available
      case "new_user":
        return [
          { label: "Approve", value: "approve" },
          { label: "Block", value: "block" },
          ...(userRole === "super_admin"
            ? [{ label: "Promote to Admin", value: "admin" }]
            : []),
        ];
      case "approved_user":
        return [
          { label: "Block", value: "block" },
          ...(userRole === "super_admin"
            ? [{ label: "Promote to Admin", value: "admin" }]
            : []),
        ];
      case "blocked_user":
        return [
          { label: "Approve", value: "approve" },
          ...(userRole === "super_admin"
            ? [{ label: "Promote to Admin", value: "admin" }]
            : []),
        ];
      default:
        return [];
    }
  };

  const availableActions = getAvailableActions(userRoleName);

  async function handleRoleChange(
    userId: string,
    roleAction: "approve" | "block" | "admin"
  ) {
    if (roleAction === "block") {
      setShowRejectionDialog(true);
      return;
    }

    setShowConfirm(roleAction);
  }

  // If no actions available, show disabled text
  if (availableActions.length === 0) {
    return null;
  }

  function handleRoleUpdate(
    userId: string,
    roleAction: "approve" | "block" | "admin"
  ) {
    updateRole(userId, roleAction);
    if (roleAction === "approve") {
      setShowMergeDialog(true);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <Ellipsis className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* <DropdownMenuLabel>Actions</DropdownMenuLabel> */}
          {availableActions.map((action) => (
            <DropdownMenuItem
              key={action.value}
              onClick={() => handleRoleChange(userID, action.value as any)}
              className="flex cursor-pointer items-center gap-0 duration-200"
            >
              {action.value === "admin" && <UserCog className="mr-2 h-4 w-4" />}
              {action.value === "approve" && (
                <CheckCheck className="mr-2 h-4 w-4" />
              )}
              {action.value === "block" && (
                <CircleMinus className="mr-2 h-4 w-4" />
              )}
              {action.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            key={"merge"}
            onClick={() =>
              nav(
                `${ROUTE_MERGE_COMPANIES}?${ROUTE_PARAM_MERGE_COMPANIES_USER_ID_KEEP}=${userID}`
              )
            }
            className="flex cursor-pointer items-center gap-0 duration-200"
          >
            <Merge className="mr-2 h-4 w-4" />
            Merge Companies
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {showRejectionDialog && (
        <UserRejectionDialog
          userFullName={userFullName}
          userID={userID}
          onClose={() => setShowRejectionDialog(false)}
          updateRole={updateRole}
        />
      )}
      {showConfirm && (
        <UserConfirmDialog
          userFullName={userFullName}
          userID={userID}
          updateRole={handleRoleUpdate}
          onClose={() => setShowConfirm(false)}
          newRole={showConfirm}
        />
      )}
      {showMergeDialog && (
        <UserMergeDialog
          userID={userID}
          onClose={() => setShowMergeDialog(false)}
        />
      )}
    </>
  );
};

export default UserApprovalRejection;
